import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faBolt, faTrophy, faSkull, faStar, faHashtag, faInfinity, faMinus, faRotate } from '@fortawesome/free-solid-svg-icons';
import { PlayerStats } from '../types/activity';
import { getEloColor } from '../utils/ladder';

/** Formatiert UTC-ISO-String nach lokaler Zeit: "D.M.YY, HH:MM" */
function formatLastSync(isoUtc: string): string {
  try {
    const d = new Date(isoUtc.endsWith('Z') ? isoUtc : isoUtc + 'Z');
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    const yy = d.getFullYear().toString().slice(-2);
    return `${d.getDate()}.${d.getMonth() + 1}.${yy}, ${hh}:${mm}`;
  } catch {
    return isoUtc;
  }
}

interface Props {
  stats: PlayerStats | null | false | undefined;
  name: string;
  isMine: boolean;
}

export default function PlayerStatsTooltip({ stats, name, isMine }: Props) {
  const colorClass = isMine ? 'avatar-stats--mine' : 'avatar-stats--opp';

  if (stats === null) {
    return (
      <div className={`avatar-stats-tooltip ${colorClass}`}>
        <span className="avatar-stats-loading">Loading…</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`avatar-stats-tooltip ${colorClass}`}>
        <span className="avatar-stats-loading">Not available</span>
      </div>
    );
  }

  const ratio: React.ReactNode = stats.losses === 0
    ? (stats.wins > 0 ? `${stats.wins}:0` : <FontAwesomeIcon icon={faMinus} />)
    : `${stats.wins}:${stats.losses}`;

  const elo = (stats?.elo_rating ?? 1000);
  const color = getEloColor(elo);

  return (
    <div className={`avatar-stats-tooltip ${colorClass}`}>
      <div className="avatar-stats-name">{name}</div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faFire} className="avatar-stats-icon avatar-stats-icon--win" />
        <span className="avatar-stats-label">🔥 OnFire</span>
        <span className="avatar-stats-value" style={{ color, fontSize: '0.95rem', fontWeight: 800 }}>{elo}</span>
      </div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faBolt} className="avatar-stats-icon avatar-stats-icon--ratio" />
        <span className="avatar-stats-label">Spiele</span>
        <span className="avatar-stats-value">{stats.elo_games}</span>
      </div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faTrophy} className="avatar-stats-icon avatar-stats-icon--win" />
        <span className="avatar-stats-label">Siege</span>
        <span className="avatar-stats-value">{stats.wins}</span>
      </div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faSkull} className="avatar-stats-icon avatar-stats-icon--loss" />
        <span className="avatar-stats-label">Niederlagen</span>
        <span className="avatar-stats-value">{stats.losses}</span>
      </div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faStar} className="avatar-stats-icon avatar-stats-icon--ratio" />
        <span className="avatar-stats-label">W/L Ratio</span>
        <span className="avatar-stats-value">{ratio}</span>
      </div>
      <div className="avatar-stats-row">
        <FontAwesomeIcon icon={faHashtag} className="avatar-stats-icon avatar-stats-icon--rank" />
        <span className="avatar-stats-label">Globaler Rang</span>
        <span className="avatar-stats-value">#{stats.global_rank} / {stats.total_players}</span>
      </div>
      {stats.last_synced_at && (
        <div className="avatar-stats-row">
          <FontAwesomeIcon icon={faRotate} className="avatar-stats-icon avatar-stats-icon--ratio" />
          <span className="avatar-stats-label">Sync</span>
          <span className="avatar-stats-value" style={{ fontSize: '0.78rem' }}>{formatLastSync(stats.last_synced_at)}</span>
        </div>
      )}
    </div>
  );
}
